// src/pages/orders/orderDetailsScreen/hooks/useOrderDetails.ts
import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { getApi } from "../../../api/api";
import { selectOrderById } from "../../../store/slices/orderSlice";
import {
  selectIsExecutor,
  selectIsAdmin,
} from "../../../store/slices/executorSlice";
import { OrderInterface, TaskInterface } from "../../../common/types/types";
import {
  OrderStatus,
  PaymentStatus,
  DeliveryType,
  TaskStatus,
  TaskType,
} from "../../../common/types/enums";
import {
  getDeliveryTypeText,
  getOrderStatusText,
  getPaymentMethodText,
  getPaymentStatusText,
  getStatusColor,
  getStatusTextColor,
} from "../../../common/utils/functions/enumToText";
import {
  getDeliveryTypeIcon,
  getOrderStatusIcon,
  getPaymentMethodIcon,
  getPaymentStatusIcon,
} from "../../../common/utils/functions/enumToIcon";
import { delayAction } from "../../../common/utils/functions/delayAction";
import useInterval from "@use-it/interval";
import { reloadUserData } from "../../../common/utils/functions/reloadUserData";
import { useCopyToClipboard } from "../../../common/hooks/useCopyToClipboard";
import {
  MdApartment,
  MdMeetingRoom,
  MdStairs,
  MdPhoneInTalk,
} from "react-icons/md";

const POLL_INTERVAL = 5 * 60 * 1000;

const orderTransitions: Record<number, number[]> = {
  [OrderStatus.NEW]: [OrderStatus.PREPARING, OrderStatus.CANCELLED],
  [OrderStatus.PREPARING]: [OrderStatus.READY, OrderStatus.CANCELLED],
  [OrderStatus.READY]: [
    OrderStatus.DELIVERING,
    OrderStatus.PICKED_UP,
    OrderStatus.CANCELLED,
  ],
  [OrderStatus.DELIVERING]: [OrderStatus.DELIVERED, OrderStatus.CANCELLED],
  [OrderStatus.DELIVERED]: [],
  [OrderStatus.PICKED_UP]: [],
  [OrderStatus.CANCELLED]: [],
};

export const useOrderDetails = (orderId?: number) => {
  const [api] = useState(getApi);
  const orderFromStore = useSelector(selectOrderById(Number(orderId)));
  const isExecutor = useSelector(selectIsExecutor);
  const isAdmin = useSelector(selectIsAdmin);
  const canManage = isExecutor || isAdmin;
  const [order, setOrder] = useState<OrderInterface | undefined>(
    orderFromStore,
  );
  const [updating, setUpdating] = useState(false);
  const [taskUpdating, setTaskUpdating] = useState(false);
  const [ready, setReady] = useState(false);
  const { copy, copied } = useCopyToClipboard();

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const result = await api.getOrder(orderId);
      if (result) setOrder(result);
    } catch (error) {
      console.error("Failed to fetch order:", error);
    }
  }, [orderId, api]);

  useEffect(() => {
    const init = async () => {
      await delayAction(500);
      setReady(true);
    };
    init();
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (orderFromStore) {
      setOrder(orderFromStore);
    } else {
      fetchOrder();
    }
  }, [ready, orderFromStore, fetchOrder]);

  // ❌ активный таск доставки
  const activeTask: TaskInterface | undefined = useMemo(() => {
    if (!order?.tasks?.length) return undefined;
    return order.tasks.find(
      (t) =>
        t.type === TaskType.DELIVERY &&
        t.status !== TaskStatus.CANCELLED &&
        t.status !== TaskStatus.DELIVERED,
    );
  }, [order?.tasks]);

  const getOrderStatusInfo = useCallback((status: OrderStatus) => {
    return {
      text: getOrderStatusText(status),
      icon: getOrderStatusIcon(status),
      backgroundColor: getStatusColor(status),
      textColor: getStatusTextColor(status),
    };
  }, []);

  const hasActiveTransition =
    order?.transitions && order.transitions.length > 0;
  const transitionEndTime =
    hasActiveTransition && order?.transitions
      ? order.transitions[order.transitions.length - 1].executeAt
      : null;

  const isFinalStatus = order
    ? [
        OrderStatus.PICKED_UP,
        OrderStatus.DELIVERED,
        OrderStatus.CANCELLED,
      ].includes(order.status)
    : true;

  const canStartTransition = order
    ? order.status === OrderStatus.NEW && !hasActiveTransition
    : false;

  const canManualTransition = canManage && !isFinalStatus;

  const availableTransitions = order
    ? orderTransitions[order.status] || []
    : [];

  const handleStatusUpdate = useCallback(
    async (status: OrderStatus) => {
      if (!order?.id) return;
      setUpdating(true);
      try {
        await api.updateOrderStatusByExecutor(order.id, status);
        await fetchOrder();
        await reloadUserData();
      } catch (error) {
        console.error("Failed to update status:", error);
      } finally {
        setUpdating(false);
      }
    },
    [order, api, fetchOrder],
  );

  const handleMarkPaid = useCallback(async () => {
    if (!order?.id) return;
    setUpdating(true);
    try {
      await api.updateOrderPaymentStatus(order.id, PaymentStatus.PAID);
      await fetchOrder();
      await reloadUserData();
    } catch (error) {
      console.error("Failed to mark as paid:", error);
    } finally {
      setUpdating(false);
    }
  }, [order, api, fetchOrder]);

  // ❌ создание таска
  const handleCreateTask = useCallback(
    async (type: TaskType = TaskType.DELIVERY) => {
      if (!order?.id) return;
      setTaskUpdating(true);
      try {
        await api.createTask({ orderId: order.id, type });
        await fetchOrder();
      } catch (error) {
        console.error("Failed to create task:", error);
      } finally {
        setTaskUpdating(false);
      }
    },
    [order, api, fetchOrder],
  );

  // ❌ отмена таска
  const handleCancelTask = useCallback(
    async (taskId: number, comment?: string) => {
      setTaskUpdating(true);
      try {
        await api.cancelTask(taskId, { comment });
        await fetchOrder();
      } catch (error) {
        console.error("Failed to cancel task:", error);
      } finally {
        setTaskUpdating(false);
      }
    },
    [api, fetchOrder],
  );

  // ❌ смена статуса таска
  const handleUpdateTaskStatus = useCallback(
    async (taskId: number, status: TaskStatus, comment?: string) => {
      setTaskUpdating(true);
      try {
        await api.updateTaskStatus(taskId, { status, comment });
        await fetchOrder();
      } catch (error) {
        console.error("Failed to update task status:", error);
      } finally {
        setTaskUpdating(false);
      }
    },
    [api, fetchOrder],
  );

  // --- Логика копирования адреса ---
  const fullAddress = order?.fullAddress || null;

  const getAddressDetails = useCallback(() => {
    if (!fullAddress) return [];
    const details = [];
    if (fullAddress.apartment)
      details.push({ icon: MdApartment, text: `кв. ${fullAddress.apartment}` });
    if (fullAddress.entrance)
      details.push({
        icon: MdMeetingRoom,
        text: `под. ${fullAddress.entrance}`,
      });
    if (fullAddress.floor)
      details.push({ icon: MdStairs, text: `эт. ${fullAddress.floor}` });
    if (fullAddress.intercom)
      details.push({
        icon: MdPhoneInTalk,
        text: `домофон ${fullAddress.intercom}`,
      });
    return details;
  }, [fullAddress]);

  const getFullAddressString = useCallback(() => {
    if (!fullAddress) return "";

    const parts = [];

    if (fullAddress.city?.value) {
      parts.push(`г. ${fullAddress.city.value}`);
    }

    if (fullAddress.address?.value) {
      parts.push(fullAddress.address.value);
    }

    if (fullAddress.apartment) {
      parts.push(`кв. ${fullAddress.apartment}`);
    }

    if (fullAddress.entrance) {
      parts.push(`под. ${fullAddress.entrance}`);
    }

    if (fullAddress.floor) {
      parts.push(`эт. ${fullAddress.floor}`);
    }

    if (fullAddress.intercom) {
      parts.push(`домофон ${fullAddress.intercom}`);
    }

    if (fullAddress.description) {
      parts.push(`Комментарий: ${fullAddress.description}`);
    }

    return parts.join(", ");
  }, [fullAddress]);

  const getPickupAddressString = useCallback(() => {
    if (!order?.store) return "";

    const parts = [];

    if (order.store.name) {
      parts.push(order.store.name);
    }

    if (order.store.city?.value) {
      parts.push(`г. ${order.store.city.value}`);
    }

    if (order.store.address?.value) {
      parts.push(order.store.address.value);
    }

    return parts.join(", ");
  }, [order?.store]);

  const handleCopyAddress = useCallback(() => {
    if (!order) return;

    let addressString = "";

    if (Number(order.deliveryType) === DeliveryType.DELIVERY) {
      addressString = getFullAddressString();
    } else if (Number(order.deliveryType) === DeliveryType.PICKUP) {
      addressString = getPickupAddressString();
    }

    if (addressString) {
      copy(addressString);
    }
  }, [order, getFullAddressString, getPickupAddressString, copy]);

  useInterval(() => {
    if (!isFinalStatus) {
      fetchOrder();
    }
  }, POLL_INTERVAL);

  return {
    order,
    updating,
    taskUpdating,
    refreshOrder: fetchOrder,
    getOrderStatusInfo,
    getDeliveryTypeText,
    getPaymentMethodText,
    getPaymentStatusText,
    getDeliveryTypeIcon,
    getPaymentMethodIcon,
    getPaymentStatusIcon,
    hasActiveTransition,
    transitionEndTime,
    isFinalStatus,
    canStartTransition,
    canManualTransition,
    availableTransitions,
    handleStatusUpdate,
    handleMarkPaid,
    isAdmin,
    canManage,
    // ❌ задачи
    activeTask,
    handleCreateTask,
    handleCancelTask,
    handleUpdateTaskStatus,
    // Новые методы для копирования адреса
    fullAddress,
    addressDetails: getAddressDetails(),
    handleCopyAddress,
    copied,
  };
};
